import React from 'react';

import {
  Grid,
  Section,
  ButtonGroup,
  Button,
  EditNoteIcon,
  UploadFileIcon,
} from '@gogame/components';
import { withForm, SidePageV2, FormFieldArray } from '@gogame/form';
import { useChooseFiles } from '@gogame/hooks-ts';
import { useScreenModalV2 } from '@gogame/screens';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  DesignerAndOutsourceStudioProvider,
  StudioType,
  StudioComponent,
} from 'scenes/Gallery/GalleryPage/Creatives/components/DesignerAndOutsourceStudio';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import * as CONCEPTS from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  CREATIVE_TYPE_BANNER,
  CREATIVE_TYPE_VIDEO,
} from 'scenes/Gallery/GalleryPage/constants';
import {
  loadRequestedBy,
  loadAllBusinessPartners,
} from 'scenes/Gallery/GalleryPage/Creatives/sources/fields';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

import CreativePrefillFieldsDialog from './CreativePrefillFieldsDialog';
import { CopyCreativeFieldsProvider } from './CopyCreativeFields';
import Creative from './Creative';
import RootBusinessPartnersFormField from './components/RootBusinessPartnersFormField';
import RootJiraTaskFormField from './components/RootJiraTaskFormField';
import RootRequestedByFormField from './components/RootRequestedByFormField';

const CreateCreativesForm = withForm({
  initialNormalized: false,
  enableReinitialize: true,
})(({
  children,
  loading,
  values,
  setValues,
  setFieldValue,
  onClose,
  title,
  subTitle,
  concept,
  original,
  processAsync,
  onExited,
  open,
}) => {
  const chooseFiles = useChooseFiles();
  const {
    isAllowDesigner,
    isAllowJiraTask,
    isAllowRequestedBy,
    isAllowBusinessPartner,
    isAllowOutsourceStudio,
    isAllowExclusiveForAffiliates,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();

  const handleAddFiles = async () => {
    const newFiles = await chooseFiles();

    if (!newFiles.length) {
      return;
    }

    const newRows = newFiles.map((file) => {
      let creativeResult = {
        [CREATIVE.FILE]: file,
        [CREATIVE.INHERIT_TAGS]: concept?.[CONCEPT.TAGS],
        [CREATIVE.NETWORKS]: original[CREATIVE.NETWORKS],
        [CREATIVE.LANGUAGE]: original[CREATIVE.LANGUAGE],
        [CREATIVE.CHANNELS]: original[CREATIVE.CHANNELS],
        [CREATIVE.COLOR]: original[CREATIVE.COLOR],
        [CREATIVE.OS]: original[CREATIVE.OS],
        [CREATIVE.ENHANCERS]: original[CREATIVE.ENHANCERS],
        [CREATIVE.APPLICATION]: original[CREATIVE.APPLICATION],
        [CREATIVE.AI_GENERATED]: original[CREATIVE.AI_GENERATED],
      };

      creativeResult[CREATIVE.DESCRIPTION] =
        `Original File Name: ${file?.name}`;

      if (isAllowDesigner) {
        creativeResult[CREATIVE.DESIGNER] = original[CREATIVE.DESIGNER];
      }

      if (isAllowOutsourceStudio) {
        creativeResult[CREATIVE.OUTSOURCE_STUDIO_ID] =
          original[CREATIVE.OUTSOURCE_STUDIO_ID];
      }

      if (isAllowJiraTask) {
        creativeResult[CREATIVE.JIRA_TASK] = original[CREATIVE.JIRA_TASK];
      }

      if (isAllowRequestedBy) {
        creativeResult[CREATIVE.REQUESTED_BY] = original[CREATIVE.REQUESTED_BY];
      }

      if (isAllowBusinessPartner) {
        creativeResult[CREATIVE.BUSINESS_PARTNERS] =
          original[CREATIVE.BUSINESS_PARTNERS];
      }

      if (isAllowProductionType) {
        creativeResult[CREATIVE.PRODUCTION_TYPE] =
          original[CREATIVE.PRODUCTION_TYPE];
      }

      if (isAllowTested) {
        creativeResult[CREATIVE.TESTED] = original[CREATIVE.TESTED];
      }

      return creativeResult;
    });

    setFieldValue(CREATIVE.CREATIVES, [
      ...values[CREATIVE.CREATIVES],
      ...newRows,
    ]);
  };

  const showModal = useScreenModalV2(CreativePrefillFieldsDialog);

  const handlePrefillFields = async () => {
    const { creativeData, conceptData } = await showModal();

    const {
      [CREATIVE.COLOR]: color,
      [CREATIVE.APPLICATION]: application,
      [CREATIVE.BUSINESS_PARTNERS]: businessPartners,
      [CREATIVE.NETWORKS]: networks,
      [CREATIVE.CHANNELS]: channels,
      [CREATIVE.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
      [CREATIVE.OS]: os,
      [CREATIVE.LANGUAGE]: language,
      [CREATIVE.ENHANCERS]: enhancers,
      [CREATIVE.TAGS]: internalTags,
      [CREATIVE.INHERIT_TAGS]: inheritInternalTags,
      [CREATIVE.TESTED]: tested,
      [CREATIVE.OUTSOURCE_STUDIO_ID]: outsourceStudio,
      [CREATIVE.DESIGNER]: designer,
      [CREATIVE.POSTFIX]: postfix,
      [CREATIVE.YOUTUBE_LINK]: youtubeLink,
      [CREATIVE.AI_GENERATED]: aiGenerated,
      [CREATIVE.PRODUCTION_TYPE]: productionType,
    } = creativeData;

    const { [CONCEPTS.APPLICATIONS]: conceptApplications } = conceptData;

    setValues({
      ...values,
      [CREATIVE.CREATIVES]: values[CREATIVE.CREATIVES].map((creative) => {
        if (
          !(
            creative[CREATIVE.CREATIVE_TYPE] === CREATIVE_TYPE_BANNER ||
            creative[CREATIVE.CREATIVE_TYPE] === CREATIVE_TYPE_VIDEO
          )
        ) {
          return creative;
        }

        return {
          ...creative,
          [CREATIVE.COLOR]: color,
          [CREATIVE.APPLICATION]: application || conceptApplications[0],
          [CREATIVE.NETWORKS]: networks,
          [CREATIVE.CHANNELS]: channels,
          ...(isAllowExclusiveForAffiliates
            ? {
                [CREATIVE.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
              }
            : null),
          [CREATIVE.OS]: os,
          [CREATIVE.LANGUAGE]: language,
          [CREATIVE.TAGS]: internalTags,
          [CREATIVE.INHERIT_TAGS]: inheritInternalTags,
          [CREATIVE.POSTFIX]: postfix,
          [CREATIVE.ENHANCERS]: enhancers,
          ...(isAllowTested
            ? {
                [CREATIVE.TESTED]: tested,
              }
            : null),
          ...(isAllowOutsourceStudio
            ? {
                [CREATIVE.OUTSOURCE_STUDIO_ID]: outsourceStudio,
              }
            : null),
          ...(isAllowDesigner
            ? {
                [CREATIVE.DESIGNER]: designer,
              }
            : null),
          ...(isAllowBusinessPartner
            ? {
                [CONCEPT.BUSINESS_PARTNERS]: businessPartners,
              }
            : null),
          [CREATIVE.YOUTUBE_LINK]: youtubeLink,
          [CREATIVE.AI_GENERATED]: aiGenerated,
          ...(isAllowProductionType
            ? {
                [CREATIVE.PRODUCTION_TYPE]: productionType,
              }
            : null),
        };
      }),
    });
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      fullScreen={true}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} />
      <SidePageV2.NewBody loading={loading}>
        <DesignerAndOutsourceStudioProvider>
          <Section
            title={'Default Values'}
            subTitle={
              'Enter values that will be applied to all uploaded creatives'
            }
            action={
              <ButtonGroup>
                {values[CREATIVE.CREATIVES].some(
                  (creative) =>
                    creative[CREATIVE.CREATIVE_TYPE] === CREATIVE_TYPE_BANNER ||
                    creative[CREATIVE.CREATIVE_TYPE] === CREATIVE_TYPE_VIDEO
                ) ? (
                  <Button
                    variant={'outlined'}
                    icon={<EditNoteIcon />}
                    onClick={handlePrefillFields}
                  >
                    Prefill fields from...
                  </Button>
                ) : null}
                <Button
                  variant={'outlined'}
                  icon={<UploadFileIcon />}
                  onClick={handleAddFiles}
                >
                  Select Files
                </Button>
              </ButtonGroup>
            }
          >
            <Grid
              container
              spacing={3}
              columns={8}
              alignItems={'center'}
              mb={2}
            >
              <Grid item xs={2}>
                {isAllowBusinessPartner ? (
                  <RootBusinessPartnersFormField
                    label={'On behalf of BP'}
                    name={CREATIVE.BUSINESS_PARTNERS}
                    source={loadAllBusinessPartners}
                    padding={0}
                  />
                ) : null}
              </Grid>

              <Grid item xs={3}>
                {isAllowJiraTask ? (
                  <RootJiraTaskFormField
                    label={'Jira task URL'}
                    name={CREATIVE.JIRA_TASK}
                    placeholder={'Jira task URL'}
                    padding={0}
                  />
                ) : null}
              </Grid>

              <Grid item xs={1}>
                {isAllowRequestedBy ? (
                  <RootRequestedByFormField
                    label={'Requested by'}
                    name={CREATIVE.REQUESTED_BY}
                    source={loadRequestedBy}
                    padding={0}
                  />
                ) : null}
              </Grid>

              <Grid item xs={1}>
                {isAllowDesigner ? <StudioType /> : null}
              </Grid>

              <Grid item xs={1}>
                {isAllowDesigner && <StudioComponent />}
              </Grid>
            </Grid>
          </Section>
          <CopyCreativeFieldsProvider>
            <FormFieldArray name={CREATIVE.CREATIVES}>
              {({ remove }) =>
                values[CREATIVE.CREATIVES].map((creative, index, creatives) => {
                  const { [CREATIVE.FILE]: file } = creative;
                  return (
                    <Creative
                      key={`${file.name}${file.size}`}
                      name={`${CREATIVE.CREATIVES}[${index}]`}
                      creative={creative}
                      remove={() => remove(index)}
                      processAsync={processAsync}
                      isUniqueYoutubeLink={(value) =>
                        [...new Array(index)].every(
                          (_, i) =>
                            creatives[i][CREATIVE.YOUTUBE_LINK] !== value
                        )
                      }
                    />
                  );
                })
              }
            </FormFieldArray>
          </CopyCreativeFieldsProvider>
        </DesignerAndOutsourceStudioProvider>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default CreateCreativesForm;
